/** @jsxImportSource @emotion/react */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'

import { ElementFieldConfig, FilterableType, TypeCategory } from '@app/modules/Home/Data/types'

const VULNERABILITY_CHARACTERISTICS = [
  'abutment_type',
  'base_depth',
  'basement',
  'bridge',
  'bridge_type',
  'build_date',
  'buried',
  'capital_value',
  'category',
  'condition',
  'contaminant_group',
  'cross_section',
  'deck_material',
  'deprivation_index',
  'depth',
  'depth_to_invert',
  'design_life',
  'diameter',
  'employment',
  'floor_level',
  'gross_weight_limit',
  'ground_height',
  'height',
  'hierarchy',
  'improvements_value',
  'install_date',
  'lanes',
  'long_section',
  'maintenance',
  'material',
  'material_chamber',
  'material_code',
  'material_cover',
  'number_of_units',
  'pier_type',
  'pressure_class',
  'property_type',
  'replacement_cost',
  'responsibility',
  'road_hierarchy',
  'surface',
  'surface_material',
  'width',
  'year_built',
] as const

type Props = {
  config: ElementFieldConfig
  category: TypeCategory
  onChange: (updates: Partial<ElementFieldConfig>) => void
}

const FieldConfigRow = ({ config, category, onChange }: Props) => {
  const isString = category === 'string'
  const isNumeric = category === 'numeric'
  const isDate = category === 'date'

  return (
    <Accordion disableGutters variant="outlined" sx={{ '&:before': { display: 'none' } }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          gap: 1,
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            my: 1,
          },
        }}
      >
        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 120 }}>
          {config.fieldName}
        </Typography>
        <Chip
          label={
            category === 'other'
              ? config.gdalType
              : category.charAt(0).toUpperCase() + category.slice(1)
          }
          size="small"
          variant="outlined"
        />
        {config.ignore ? (
          <Chip label="Ignored" size="small" />
        ) : (
          config.key !== config.fieldName && (
            <Typography variant="caption" color="text.secondary">
              → {config.key}
            </Typography>
          )
        )}
        <Box sx={{ flex: 1 }} />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={config.ignore}
              onClick={(e) => e.stopPropagation()}
              onChange={(_, checked) => onChange({ ignore: checked })}
            />
          }
          label={<Typography variant="caption">Ignore</Typography>}
          onClick={(e) => e.stopPropagation()}
          sx={{ mr: 1 }}
        />
      </AccordionSummary>

      {!config.ignore && (
        <AccordionDetails>
          <Stack spacing={2}>
            <TextField
              label="Field name"
              size="small"
              value={config.key}
              onChange={(e) => {
                const newKey = e.target.value
                const updates: Partial<ElementFieldConfig> = { key: newKey }
                // Keep filterableLabel in sync while it hasn't been customised
                if (config.filterableLabel === config.key)
                  updates.filterableLabel = newKey || config.fieldName
                onChange(updates)
              }}
              fullWidth
            />

            {/* Type-specific formatting options */}
            {(isString || isNumeric || isDate) && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Formatting
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {isString && (
                    <>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={config.titleCase}
                            onChange={(_, v) => onChange({ titleCase: v })}
                          />
                        }
                        label={<Typography variant="body2">Title case</Typography>}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={config.searchData}
                            onChange={(_, v) => onChange({ searchData: v })}
                          />
                        }
                        label={<Typography variant="body2">Search data</Typography>}
                      />
                    </>
                  )}
                  {isNumeric && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={config.toInt}
                          onChange={(_, v) => onChange({ toInt: v })}
                        />
                      }
                      label={<Typography variant="body2">Convert to integer</Typography>}
                    />
                  )}
                  {(isNumeric || isString) && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={config.elseAsNone}
                          onChange={(_, v) => onChange({ elseAsNone: v })}
                        />
                      }
                      label={<Typography variant="body2">Else as none</Typography>}
                    />
                  )}
                  {isDate && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={config.fromEpoch}
                          onChange={(_, v) => onChange({ fromEpoch: v })}
                        />
                      }
                      label={<Typography variant="body2">From epoch</Typography>}
                    />
                  )}
                </Stack>
              </Box>
            )}

            <Autocomplete
              freeSolo
              options={VULNERABILITY_CHARACTERISTICS}
              value={config.vulnerabilityCharacteristics}
              onInputChange={(_, value) => onChange({ vulnerabilityCharacteristics: value })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vulnerability characteristics"
                  size="small"
                  placeholder="Select or type a value"
                />
              )}
            />

            {/* Filterable information */}
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={config.filterableEnabled}
                    onChange={(_, v) => onChange({ filterableEnabled: v })}
                  />
                }
                label={<Typography variant="body2">Filterable information</Typography>}
              />
              {config.filterableEnabled && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      label="Type"
                      value={config.filterableType || 'text'}
                      onChange={(e) =>
                        onChange({ filterableType: e.target.value as FilterableType })
                      }
                    >
                      <MenuItem value="numeric">Numeric</MenuItem>
                      <MenuItem value="date">Date</MenuItem>
                      <MenuItem value="text">Categorical</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Label"
                    size="small"
                    value={config.filterableLabel}
                    onChange={(e) => onChange({ filterableLabel: e.target.value })}
                    sx={{ flex: 1 }}
                  />
                </Stack>
              )}
            </Box>

            {/* Report fields */}
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={config.reportEnabled}
                    onChange={(_, v) => onChange({ reportEnabled: v })}
                  />
                }
                label={<Typography variant="body2">Report fields</Typography>}
              />
              {config.reportEnabled && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <TextField
                    label="Alias"
                    size="small"
                    value={config.reportAlias}
                    onChange={(e) => onChange({ reportAlias: e.target.value })}
                    sx={{ flex: 1 }}
                  />
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>Confidence</InputLabel>
                    <Select
                      label="Confidence"
                      value={config.reportConfidence}
                      onChange={(e) => onChange({ reportConfidence: e.target.value })}
                    >
                      <MenuItem value="">None</MenuItem>
                      {['A', 'B', 'C', 'D', 'E'].map((grade) => (
                        <MenuItem key={grade} value={grade}>
                          {grade}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              )}
            </Box>

            {/* Value mapping — not applicable for date fields */}
            {!isDate && (
              <>
                <TextField
                  label="Replace with None"
                  size="small"
                  placeholder="0, Unknown, Not Applicable"
                  helperText="Comma-separated values to treat as None"
                  value={config.replaceWithNone}
                  onChange={(e) => onChange({ replaceWithNone: e.target.value })}
                  fullWidth
                />
                {/* @TODO: Lazy version with : delimiter, should be more wizard like with adding transforms */}
                {isString && (
                  <TextField
                    label="Rename values"
                    size="small"
                    placeholder="RETC:Reticulation, RES:Residential"
                    helperText="OLD:NEW pairs, comma-separated"
                    value={config.renameValues}
                    onChange={(e) => onChange({ renameValues: e.target.value })}
                    fullWidth
                  />
                )}
              </>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!config.sentenceExclusion}
                  onChange={(_, v) => onChange({ sentenceExclusion: v })}
                />
              }
              label={<Typography variant="body2">Sentence exclusion</Typography>}
            />
          </Stack>
        </AccordionDetails>
      )}
    </Accordion>
  )
}

export default FieldConfigRow
